import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";

import { useVolatileCallback } from "./useVolatile";

// https://usehooks.com/useDebounce/
export const useDebounce = <T>(value: T, delay: number) => {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay], // Only re-call effect if value or delay changes
  );

  return debouncedValue;
};

export const useDebouncedCallback = <T extends (...args: any) => any>(
  callback: T,
  delay = 500,
) => {
  //
  // Ensure that the debounce does not call stale instances of the callback
  //
  const volatileCallback = useVolatileCallback(callback);

  const debounced = useRef(debounce(volatileCallback, delay)).current;
  return debounced;
};

export const useDebouncedState = <T>(
  initialValue: T,
  timeout?: number,
): [value: T, debouncedValue: T, setValue: (value: T) => void] => {
  const [value, setValue] = useState(initialValue);
  const [debouncedValue, setDebouncedValue] = useState(initialValue);

  const setDebouncedValueDebounced = useDebouncedCallback(
    setDebouncedValue,
    timeout,
  );

  return [
    value,
    debouncedValue,
    useCallback(
      (value) => {
        setValue(value);
        setDebouncedValueDebounced(value);
      },
      [setValue, setDebouncedValueDebounced],
    ),
  ];
};

export default useDebounce;
